import React, { useState, useEffect, useCallback } from 'react';
import TopNavigation from 'components/ui/TopNavigation';
import Icon from 'components/AppIcon';
import api from 'lib/api';
import { useAuth } from 'contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import WorkoutStats from './components/WorkoutStats';
import MeasurementTracking from './components/MeasurementTracking';
import PersonalRecords from './components/PersonalRecords';
import AchievementBadges from './components/AchievementBadges';
import GoalProgress from './components/GoalProgress';

function SkeletonBlock({ className }) {
  return <div className={`bg-[var(--color-muted)] animate-pulse rounded ${className}`} />;
}

export default function MemberProgress() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('workout');
  const [memberRecord, setMemberRecord] = useState(null);
  const [memberLoading, setMemberLoading] = useState(true);

  useEffect(() => {
    const fetchMember = async () => {
      if (!user?.email) { setMemberLoading(false); return; }
      try {
        const { data: members } = await api.get('/members', { params: { search: user?.email, limit: 1 } });
        setMemberRecord(members?.[0] ?? null);
      } catch (_) {}
      finally { setMemberLoading(false); }
    };
    fetchMember();
  }, [user?.email]);

  const tabs = [
    { id: 'workout', label: 'Workouts', icon: 'Dumbbell' },
    { id: 'measurements', label: 'Measurements', icon: 'Ruler' },
    { id: 'records', label: 'Records', icon: 'Trophy' },
    { id: 'badges', label: 'Badges', icon: 'Award' },
    { id: 'goals', label: 'Goals', icon: 'Target' },
  ];

  const memberName = memberRecord
    ? `${memberRecord?.first_name ?? ''} ${memberRecord?.last_name ?? ''}`?.trim()
    : user?.email ?? 'Member';

  return (
    <div className="min-h-screen bg-[var(--color-background)]">
      <TopNavigation />
      <main className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8 py-5 md:py-6 lg:py-8 pt-[76px]">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <button
                onClick={() => navigate('/member-portal')}
                className="flex items-center gap-1 text-sm text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)] transition-colors font-[var(--font-caption)]"
              >
                <Icon name="ChevronLeft" size={14} color="currentColor" />
                Member Portal
              </button>
            </div>
            <h1 className="text-2xl font-bold text-[var(--color-foreground)] font-[var(--font-heading)]">
              My Progress
            </h1>
            {memberLoading ? (
              <SkeletonBlock className="h-4 w-40 mt-1" />
            ) : (
              <p className="text-sm text-[var(--color-muted-foreground)] font-[var(--font-caption)] mt-0.5">
                Track your fitness journey, {memberName?.split(' ')?.[0] ?? 'Member'}
              </p>
            )}
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-1 overflow-x-auto pb-1 mb-6">
          <div className="flex gap-1 bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-md)] p-1 shadow-[var(--shadow-sm)] min-w-max">
            {tabs?.map(tab => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded text-sm font-medium font-[var(--font-caption)] transition-all duration-200 whitespace-nowrap ${
                  activeTab === tab?.id
                    ? 'bg-[var(--color-primary)] text-white shadow-sm'
                    : 'text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]'
                }`}
              >
                <Icon name={tab?.icon} size={15} color={activeTab === tab?.id ? '#fff' : 'currentColor'} />
                {tab?.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        {memberLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[1, 2, 3, 4]?.map(i => <SkeletonBlock key={i} className="h-48" />)}
          </div>
        ) : !memberRecord ? (
          <div className="flex flex-col items-center justify-center gap-4 py-20">
            <div className="w-16 h-16 rounded-full bg-[var(--color-muted)] flex items-center justify-center">
              <Icon name="UserX" size={28} color="var(--color-muted-foreground)" />
            </div>
            <div className="text-center">
              <h3 className="text-lg font-semibold text-[var(--color-foreground)] font-[var(--font-heading)]">Member record not found</h3>
              <p className="text-sm text-[var(--color-muted-foreground)] font-[var(--font-caption)] mt-1">
                Your member profile could not be loaded. Please contact support.
              </p>
            </div>
            <button
              onClick={() => navigate('/member-portal')}
              className="flex items-center gap-2 px-4 py-2 bg-[var(--color-primary)] text-white rounded-[var(--radius-sm)] text-sm font-medium font-[var(--font-caption)] hover:opacity-90 transition-opacity"
            >
              <Icon name="ArrowLeft" size={14} color="#fff" />
              Back to Portal
            </button>
          </div>
        ) : (
          <>
            {activeTab === 'workout' && <WorkoutStats memberId={memberRecord?.id} />}
            {activeTab === 'measurements' && <MeasurementTracking memberId={memberRecord?.id} memberGoalId={memberRecord?.id} />}
            {activeTab === 'records' && <PersonalRecords memberId={memberRecord?.id} />}
            {activeTab === 'badges' && <AchievementBadges memberId={memberRecord?.id} memberCreatedAt={memberRecord?.created_at} />}
            {activeTab === 'goals' && <GoalProgress memberId={memberRecord?.id} memberCreatedAt={memberRecord?.created_at} />}
          </>
        )}
      </main>
    </div>
  );
}
